import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { hiringApi } from '../api/hiring';
import { useProgramContext } from '../hooks/useProgramContext';

// Hiring (DA-5042), read through react-query: the owner's rounds, candidates,
// transcripts and activity, and the candidate's own assessment. Writes go
// straight through hiringApi and then refresh what they change with
// useRefreshHiring().

const hasTokens = () => {
  try {
    const tokens = JSON.parse(localStorage.getItem('tokens') || '{}');
    return Boolean(tokens?.id_token && tokens?.access_token);
  } catch {
    return false;
  }
};

const ROOT = ['hiring'] as const;

export const hiringKeys = {
  access: (org: string | undefined) => [...ROOT, 'access', org || ''] as const,
  rounds: (org: string | undefined, filter = '') => [...ROOT, 'rounds', org || '', filter] as const,
  round: (id: string, org: string | undefined) => [...ROOT, 'round', id, org || ''] as const,
  candidates: (roundId: string, org: string | undefined) => [...ROOT, 'candidates', roundId, org || ''] as const,
  candidate: (id: string, org: string | undefined) => [...ROOT, 'candidate', id, org || ''] as const,
  transcript: (id: string, org: string | undefined) => [...ROOT, 'transcript', id, org || ''] as const,
  activity: (roundId: string, org: string | undefined) => [...ROOT, 'activity', roundId, org || ''] as const,
  compare: (roundId: string, ids: string[], org: string | undefined) =>
    [...ROOT, 'compare', roundId, ids.join(','), org || ''] as const,
  me: [...ROOT, 'me'] as const
};

/** The org a platform admin is looking at; undefined for everyone else (their token carries it). */
export const useHiringOrg = (): string | undefined => {
  const { orgId, isPlatformAdmin } = useProgramContext();
  return isPlatformAdmin ? orgId : undefined;
};

const STALE = 30_000;

// ─── The owner's ────────────────────────────────────────────────────────────

/**
 * Whether this person may open Hiring at all, plus the "Using AI in hiring"
 * notice they must confirm first (L-11) and the roles they may run a round for.
 *
 * A 403 is the normal answer for most of the product's users (the org has
 * Hiring off, or they manage no team), so it is not retried and not surfaced as
 * an error: the nav item simply does not appear.
 */
export const useHiringAccess = (enabled = true) => {
  const org = useHiringOrg();
  return useQuery({
    queryKey: hiringKeys.access(org),
    queryFn: () => hiringApi.access(org),
    enabled: enabled && hasTokens(),
    staleTime: STALE,
    retry: false
  });
};

export const useHiringRounds = (filter: { status?: string; roleId?: string } = {}, enabled = true) => {
  const org = useHiringOrg();
  const key = `${filter.status || ''}|${filter.roleId || ''}`;
  return useQuery({
    queryKey: hiringKeys.rounds(org, key),
    queryFn: async () => (await hiringApi.rounds(filter, org)).rounds,
    enabled: enabled && hasTokens(),
    staleTime: STALE
  });
};

export const useHiringRound = (roundId: string | undefined) => {
  const org = useHiringOrg();
  return useQuery({
    queryKey: hiringKeys.round(roundId || '', org),
    queryFn: () => hiringApi.round(roundId as string, org),
    enabled: Boolean(roundId) && hasTokens(),
    staleTime: STALE
  });
};

export const useHiringCandidates = (roundId: string | undefined) => {
  const org = useHiringOrg();
  return useQuery({
    queryKey: hiringKeys.candidates(roundId || '', org),
    queryFn: async () => (await hiringApi.candidates(roundId as string, org)).candidates,
    enabled: Boolean(roundId) && hasTokens(),
    staleTime: STALE
  });
};

export const useHiringCandidate = (candidateId: string | undefined) => {
  const org = useHiringOrg();
  return useQuery({
    queryKey: hiringKeys.candidate(candidateId || '', org),
    queryFn: () => hiringApi.candidate(candidateId as string, org),
    enabled: Boolean(candidateId) && hasTokens(),
    staleTime: STALE
  });
};

export const useHiringTranscript = (candidateId: string | undefined, enabled = true) => {
  const org = useHiringOrg();
  return useQuery({
    queryKey: hiringKeys.transcript(candidateId || '', org),
    queryFn: () => hiringApi.transcript(candidateId as string, org),
    enabled: Boolean(candidateId) && enabled && hasTokens(),
    staleTime: STALE
  });
};

export const useHiringActivity = (roundId: string | undefined, enabled = true) => {
  const org = useHiringOrg();
  return useQuery({
    queryKey: hiringKeys.activity(roundId || '', org),
    queryFn: async () => (await hiringApi.activity(roundId as string, org)).entries,
    enabled: Boolean(roundId) && enabled && hasTokens(),
    staleTime: STALE
  });
};

/** H-19: two candidates side by side. Two, never more (the endpoint refuses otherwise). */
export const useHiringCompare = (roundId: string | undefined, ids: string[]) => {
  const org = useHiringOrg();
  return useQuery({
    queryKey: hiringKeys.compare(roundId || '', ids, org),
    queryFn: () => hiringApi.compare(roundId as string, ids, org),
    enabled: Boolean(roundId) && ids.length === 2 && hasTokens(),
    staleTime: STALE
  });
};

/**
 * What to refresh after a write. Every Hiring write changes at least the round
 * (its counts) and usually its candidates, so the default is to refresh both
 * and let react-query dedupe.
 */
export const useRefreshHiring = () => {
  const client = useQueryClient();
  return useCallback(
    (what: { roundId?: string; candidateId?: string } = {}) => {
      client.invalidateQueries({ queryKey: ROOT });
      if (what.roundId) client.invalidateQueries({ queryKey: [...ROOT, 'round', what.roundId] });
      if (what.candidateId) client.invalidateQueries({ queryKey: [...ROOT, 'candidate', what.candidateId] });
    },
    [client]
  );
};

// ─── The candidate's own ────────────────────────────────────────────────────

/**
 * The candidate's whole world. The first call also binds their new account to
 * the invite they signed up from, so it is the one read that must not be
 * cached across a sign-in.
 */
export const useHiringMe = (enabled = true) =>
  useQuery({
    queryKey: hiringKeys.me,
    queryFn: hiringApi.me,
    enabled: enabled && hasTokens(),
    staleTime: 0,
    retry: false
  });

/**
 * Every candidate step answers with the same shape, so one mutation keeps the
 * page in step with the server rather than guessing what comes next.
 */
export const useCandidateStep = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (run: () => Promise<unknown>) => run(),
    onSuccess: (next) => {
      if (next && typeof next === 'object' && 'step' in (next as Record<string, unknown>)) {
        client.setQueryData(hiringKeys.me, next);
      } else {
        client.invalidateQueries({ queryKey: hiringKeys.me });
      }
    }
  });
};
